/** Shared geometry and deterministic fixed-step simulation. */
import { courses } from "./courses";

export { courses } from "./courses";

export const DT = 1 / 120;
export const COURSE_LAYOUT_VERSION = 5;
export const BALL_RADIUS = 7.5;
export const CUP_RADIUS = 17;
export const MAX_SPEED = 700;
export const FRICTION = 130;
export const GRAVITY = 600;
const ROLLING_GRAVITY = GRAVITY * (5 / 7);
const SLOPE_FRICTION = FRICTION * 0.2;
const RICOCHET_LIMIT = 7;
const RICOCHET_WINDOW_SECONDS = 0.45;

export interface Point {
  x: number;
  y: number;
}

export interface Velocity {
  vx: number;
  vy: number;
}

export interface Ball extends Point, Velocity {
  rotation: number;
  time: number;
  holed: boolean;
  /** Bottom of the ball above the base terrain; omitted states resolve to the surface. */
  z?: number;
  vz?: number;
  airborne?: boolean;
  lie?: Point;
  resetCount?: number;
  teeProtected?: boolean;
  ricochetCount?: number;
  ricochetWindowStartedAt?: number;
}

export interface SandTrap extends Point {
  rx: number;
  ry: number;
}

export interface BoostPad extends Point {
  angle: number;
  radius: number;
  speed: number;
}

export interface Bumper extends Point {
  kick: number;
  radius: number;
}

/** A raised, playable surface with solid sides. Heights are world units. */
export interface ElevatedArea {
  bridge?: boolean;
  height: number;
  points: Point[];
}

/** A broad, deterministic transition between the base course and an elevated area. */
export interface ElevationRamp {
  /** Leaving the uphill lip carries the ball into a ballistic flight. */
  launch?: boolean;
  end: Point;
  endHeight: number;
  start: Point;
  startHeight: number;
  width: number;
}

export type CourseTheme = "grove" | "dunes" | "coast" | "twilight";

export interface CourseVariant {
  bumpers?: Bumper[];
  boosts: BoostPad[];
  hint: string;
  id: string;
  obstacles: Point[][];
  sand: SandTrap[];
}

export interface Wall {
  a: Point;
  b: Point;
}

export interface Course {
  /** Only the connected terraces and ramps are playable; the valley below is out of bounds. */
  baseIsHazard?: boolean;
  difficulty?: "Leicht" | "Mittel" | "Schwer" | "Sehr schwer";
  route?: Point[];
  openEdges?: boolean;
  boundary: Point[];
  name: string;
  par: number;
  theme: CourseTheme;
  turf: string;
  hint: string;
  start: Point;
  cup: Point;
  boosts: BoostPad[];
  bumpers?: Bumper[];
  elevatedAreas?: ElevatedArea[];
  elevationRamps?: ElevationRamp[];
  /** Terrain openings. Elevated surfaces can span them as bridges. */
  gaps?: Point[][];
  obstacles: Point[][];
  sand: SandTrap[];
  variants: CourseVariant[];
}

export interface PreparedCourse extends Course {
  boundary: Point[];
  variantId: string;
  walls: Wall[];
}

export type SimulationEvent =
  | { type: "holed" }
  | { type: "rest" }
  | { type: "reset" }
  | { type: "launch" }
  | { type: "land"; impact: number }
  | { type: "rolling"; impact: number; fastCup: boolean }
  | { type: "bounce"; impact: number; fastCup: boolean }
  | { type: "boost"; speed: number }
  | { type: "collision"; impact: number };

export interface ShotVelocity extends Velocity {
  power: number;
}

export interface FlightPoint extends Point {
  height: number;
}

export interface Prediction {
  points: FlightPoint[];
  holed: boolean;
  end: Ball;
}

function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(high, value));
}

function speedOf(velocity: Velocity): number {
  return Math.hypot(velocity.vx, velocity.vy);
}

function edges(points: Point[]): Wall[] {
  return points.map((point, index) => ({
    a: point,
    b: points[(index + 1) % points.length],
  }));
}

export function courseVariantIndex(room: string, round: number, index: number): number {
  const course = courses[index];
  if (!course || course.variants.length === 0) return 0;

  let hash = 2_166_136_261;
  for (const character of room) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16_777_619);
  }
  return (Math.abs(hash) + Math.max(0, round - 1)) % course.variants.length;
}

export function prepare(index: number, variantIndex = 0): PreparedCourse {
  const course = courses[index];
  if (!course) {
    throw new RangeError(`Unknown course index: ${index}`);
  }

  const variant = course.variants[variantIndex % course.variants.length] ?? course.variants[0];
  if (!variant) throw new Error(`Course ${index} must define at least one variant.`);

  const signedArea = course.boundary.reduce((area, p, i) => {
    const next = course.boundary[(i + 1) % course.boundary.length];
    return area + p.x * next.y - next.x * p.y;
  }, 0);
  const boundary = signedArea < 0 ? [...course.boundary].reverse() : course.boundary;
  const playable = {
    ...course,
    bumpers: [...(course.bumpers ?? []), ...(variant.bumpers ?? [])],
    boosts: [...course.boosts, ...variant.boosts],
    hint: variant.hint,
    obstacles: [...course.obstacles, ...variant.obstacles],
    sand: [...course.sand, ...variant.sand],
  };

  return {
    ...playable,
    boundary,
    variantId: variant.id,
    walls: [...(course.openEdges ? [] : edges(boundary)), ...playable.obstacles.flatMap(edges)],
  };
}

export function ballAt(point: Point): Ball {
  return {
    x: point.x,
    y: point.y,
    vx: 0,
    vy: 0,
    rotation: 0,
    time: 0,
    holed: false,
    lie: { x: point.x, y: point.y },
    ricochetCount: 0,
    ricochetWindowStartedAt: 0,
  };
}

export function pointInPolygon(point: Point, polygon: readonly Point[]): boolean {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const current = polygon[index];
    const prior = polygon[previous];
    if (!current || !prior) continue;
    const crosses = current.y > point.y !== prior.y > point.y;
    const atIntersection =
      point.x < ((prior.x - current.x) * (point.y - current.y)) / (prior.y - current.y) + current.x;
    if (crosses && atIntersection) inside = !inside;
  }
  return inside;
}

export function elevatedAreaAt(
  point: Point,
  course: Pick<Course, "elevatedAreas">,
): ElevatedArea | undefined {
  let highest: ElevatedArea | undefined;
  for (const area of course.elevatedAreas ?? []) {
    if ((!highest || area.height > highest.height) && pointInPolygon(point, area.points))
      highest = area;
  }
  return highest;
}

export function rampAt(
  point: Point,
  course: Pick<Course, "elevationRamps">,
): ElevationRamp | undefined {
  return course.elevationRamps?.find((ramp) => {
    const dx = ramp.end.x - ramp.start.x;
    const dy = ramp.end.y - ramp.start.y;
    const length = Math.hypot(dx, dy);
    const t = ((point.x - ramp.start.x) * dx + (point.y - ramp.start.y) * dy) / (length * length);
    return (
      length > 0 &&
      t >= 0 &&
      t <= 1 &&
      Math.abs((point.x - ramp.start.x) * dy - (point.y - ramp.start.y) * dx) / length <=
        ramp.width / 2
    );
  });
}

/** Shared height field, including continuous ramps and flat terrace tops. */
export function elevationAt(
  point: Point,
  course: Pick<Course, "elevatedAreas" | "elevationRamps">,
): number {
  let height = elevatedAreaAt(point, course)?.height ?? 0;
  const ramp = rampAt(point, course);
  if (ramp) {
    const dx = ramp.end.x - ramp.start.x;
    const dy = ramp.end.y - ramp.start.y;
    const t = ((point.x - ramp.start.x) * dx + (point.y - ramp.start.y) * dy) / (dx * dx + dy * dy);
    height = Math.max(height, ramp.startHeight + (ramp.endHeight - ramp.startHeight) * t);
  }
  return height;
}

/** Gaps and hazardous valleys have no floor; terraces and ramps provide safe surfaces. */
export function surfaceHeightAt(point: Point, course: Course): number | null {
  if (!pointInPolygon(point, course.boundary)) return null;
  const height = elevationAt(point, course);
  if (height > 0 || rampAt(point, course)) return height;
  return course.baseIsHazard || course.gaps?.some((gap) => pointInPolygon(point, gap)) ? null : 0;
}

export function ballHeight(ball: Ball, course: Course): number {
  return ball.z ?? elevationAt(ball, course);
}

export function ballMoving(ball: Ball): boolean {
  return Boolean(ball.airborne || ball.vz || speedOf(ball) > 0);
}

function slopeAt(point: Point, course: Course): Velocity {
  const ramp = rampAt(point, course);
  if (!ramp) return { vx: 0, vy: 0 };
  const dx = ramp.end.x - ramp.start.x;
  const dy = ramp.end.y - ramp.start.y;
  const slope = (ramp.endHeight - ramp.startHeight) / (dx * dx + dy * dy);
  // A higher overlapping terrace takes precedence over the ramp below it.
  const t = ((point.x - ramp.start.x) * dx + (point.y - ramp.start.y) * dy) / (dx * dx + dy * dy);
  if (
    (elevatedAreaAt(point, course)?.height ?? 0) >
    ramp.startHeight + t * (ramp.endHeight - ramp.startHeight) + 0.1
  )
    return { vx: 0, vy: 0 };
  return { vx: dx * slope, vy: dy * slope };
}

function groundForces(point: Point, course: Course) {
  const slope = slopeAt(point, course);
  const gradientSquared = slope.vx ** 2 + slope.vy ** 2;
  // Project a rolling sphere's gravity onto the slope's horizontal axes.
  const gravity = ROLLING_GRAVITY / (1 + gradientSquared);
  const resistance = gradientSquared > 0 ? SLOPE_FRICTION : FRICTION;
  const friction =
    (resistance * (inSand(point, course) ? 3.2 : 1)) / Math.sqrt(1 + gradientSquared);
  const ax = -slope.vx * gravity;
  const ay = -slope.vy * gravity;
  return { ax, ay, friction, canRest: Math.hypot(ax, ay) <= friction };
}

function resetBall(ball: Ball, course: PreparedCourse): SimulationEvent {
  const lie =
    ball.lie && surfaceHeightAt(ball.lie, course) !== null && groundForces(ball.lie, course).canRest
      ? ball.lie
      : course.start;
  ball.x = lie.x;
  ball.y = lie.y;
  ball.z = elevationAt(lie, course);
  ball.vx = ball.vy = ball.vz = 0;
  ball.airborne = false;
  ball.lie = { x: lie.x, y: lie.y };
  ball.resetCount = (ball.resetCount ?? 0) + 1;
  ball.ricochetCount = 0;
  ball.teeProtected = true;
  return { type: "reset" };
}

/** Collide with the vertical face of a terrace instead of teleporting onto it. */
function reflectLedge(ball: Ball, previous: Point, course: Course): number {
  const radius = BALL_RADIUS;
  let nx =
    elevationAt({ x: ball.x + radius, y: ball.y }, course) -
    elevationAt({ x: ball.x - radius, y: ball.y }, course);
  let ny =
    elevationAt({ x: ball.x, y: ball.y + radius }, course) -
    elevationAt({ x: ball.x, y: ball.y - radius }, course);
  if (Math.hypot(nx, ny) < 0.01) {
    nx = ball.x - previous.x;
    ny = ball.y - previous.y;
  }
  const length = Math.hypot(nx, ny) || 1;
  nx /= length;
  ny /= length;
  const approach = ball.vx * nx + ball.vy * ny;
  ball.x = previous.x;
  ball.y = previous.y;
  if (approach > 0) {
    ball.vx -= 1.65 * approach * nx;
    ball.vy -= 1.65 * approach * ny;
  }
  return Math.abs(approach);
}

function closest(start: Point, end: Point, point: Point): Point {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const segmentLengthSquared = dx * dx + dy * dy;
  const projection =
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / (segmentLengthSquared || 1);
  const amount = clamp(projection, 0, 1);

  return {
    x: start.x + dx * amount,
    y: start.y + dy * amount,
  };
}

/** Point to the next bend on long courses, or directly to the cup on short holes. */
export function routeTargetAt(point: Point, course: Pick<Course, "route" | "cup">): Point {
  const route = course.route;
  if (!route || route.length < 2) return course.cup;
  let nearest = Infinity,
    target = course.cup;
  for (let i = 0; i < route.length - 1; i++) {
    const projected = closest(route[i], route[i + 1], point);
    const distance = Math.hypot(point.x - projected.x, point.y - projected.y);
    if (distance < nearest) {
      nearest = distance;
      target =
        Math.hypot(point.x - route[i + 1].x, point.y - route[i + 1].y) < 130
          ? route[Math.min(i + 2, route.length - 1)]
          : route[i + 1];
    }
  }
  return target;
}

export function inSand(ball: Pick<Ball, "x" | "y">, course: Pick<Course, "sand">): boolean {
  return course.sand.some(
    (trap) => ((ball.x - trap.x) / trap.rx) ** 2 + ((ball.y - trap.y) / trap.ry) ** 2 < 1,
  );
}

function boostAt(ball: Pick<Ball, "x" | "y">, course: Pick<Course, "boosts">): BoostPad | null {
  return (
    course.boosts.find((pad) => Math.hypot(ball.x - pad.x, ball.y - pad.y) <= pad.radius) ?? null
  );
}

function applyBoost(ball: Ball, pad: BoostPad | null): boolean {
  if (!pad) return false;
  const directionX = Math.cos(pad.angle);
  const directionY = Math.sin(pad.angle);
  const alignment = ball.vx * directionX + ball.vy * directionY;
  if (alignment >= pad.speed * 0.98) return false;
  ball.vx = directionX * pad.speed;
  ball.vy = directionY * pad.speed;
  return true;
}

/**
 * A few fast rebounds are a satisfying bank shot. Repeated contacts in one
 * small window indicate a trapped ball between rails or bumpers, so settle it
 * deterministically and return control to the player instead of looping.
 */
function settleRicochetLoop(ball: Ball, impact: number): void {
  if (impact <= 0) return;
  const windowStartedAt = ball.ricochetWindowStartedAt ?? ball.time;
  const withinWindow = ball.time - windowStartedAt <= RICOCHET_WINDOW_SECONDS;
  const ricochetCount = withinWindow ? (ball.ricochetCount ?? 0) + 1 : 1;
  ball.ricochetCount = ricochetCount;
  ball.ricochetWindowStartedAt = withinWindow ? windowStartedAt : ball.time;

  if (ricochetCount < RICOCHET_LIMIT) return;
  ball.vx = 0;
  ball.vy = 0;
  ball.ricochetCount = 0;
  ball.ricochetWindowStartedAt = ball.time;
}

export function step(ball: Ball, course: PreparedCourse, dt = DT): SimulationEvent {
  if (ball.holed) return { type: "holed" };
  ball.z ??= elevationAt(ball, course);
  ball.vz ??= 0;
  const forces = groundForces(ball, course);
  if (!ballMoving(ball)) {
    const floor = surfaceHeightAt(ball, course);
    if (floor !== null && ball.z <= floor + 0.1) {
      if (forces.canRest) return { type: "rest" };
    } else {
      ball.airborne = true;
    }
  }

  const previous = { x: ball.x, y: ball.y };
  const previousZ = ball.z;
  const wasAirborne = Boolean(ball.airborne);
  const slope = slopeAt(previous, course);
  if (!wasAirborne) {
    const initialSpeed = speedOf(ball);
    const nextSpeed = Math.max(0, initialSpeed - forces.friction * dt);
    const ratio = initialSpeed ? nextSpeed / initialSpeed : 0;
    ball.vx = ball.vx * ratio + forces.ax * dt;
    ball.vy = ball.vy * ratio + forces.ay * dt;
  }
  ball.x += ball.vx * dt;
  ball.y += ball.vy * dt;
  ball.time += dt;
  let impact = 0;
  let launched = false;
  let landed = false;
  let floor = surfaceHeightAt(ball, course);

  if (wasAirborne) {
    ball.z += ball.vz * dt - 0.5 * GRAVITY * dt * dt;
    ball.vz -= GRAVITY * dt;
    if (floor !== null && floor > previousZ + BALL_RADIUS * 0.7) {
      impact = reflectLedge(ball, previous, course);
      floor = surfaceHeightAt(ball, course);
    }
    const previousFloor = surfaceHeightAt(previous, course);
    const aboveSurface = previousFloor === null || previousZ >= previousFloor - 0.1;
    if (floor !== null && ball.z <= floor && aboveSurface && ball.vz <= 0) {
      impact = Math.max(impact, -ball.vz * 0.25);
      ball.z = floor;
      ball.vz = 0;
      ball.airborne = false;
      ball.vx *= 0.72;
      ball.vy *= 0.72;
      landed = true;
    }
  } else {
    const ramp = rampAt(previous, course);
    const crossedLip =
      ramp?.launch &&
      (ball.x - ramp.end.x) * (ramp.end.x - ramp.start.x) +
        (ball.y - ramp.end.y) * (ramp.end.y - ramp.start.y) >
        0;
    const climbSpeed = ball.vx * slope.vx + ball.vy * slope.vy;
    if (crossedLip && climbSpeed > 35) {
      ball.airborne = true;
      ball.z = Math.max(previousZ, ramp.endHeight);
      ball.vz = climbSpeed * 1.28;
      launched = true;
    } else if (floor === null || floor < previousZ - BALL_RADIUS * 0.7) {
      ball.airborne = true;
      ball.vz = Math.max(0, climbSpeed);
    } else if (floor > previousZ + BALL_RADIUS * 0.7) {
      impact = reflectLedge(ball, previous, course);
    } else {
      ball.z = floor;
    }
  }

  impact = Math.max(
    impact,
    resolveWalls(ball, course, previous),
    resolveBumpers(ball, course, previous),
  );
  if (!ball.airborne) settleRicochetLoop(ball, impact);
  const boosted = !ball.airborne && applyBoost(ball, boostAt(ball, course));
  ball.rotation += Math.hypot(ball.x - previous.x, ball.y - previous.y) / BALL_RADIUS;
  if (ball.z < -180) return resetBall(ball, course);

  const nearestCupPoint = closest(previous, ball, course.cup);
  const cupDistance = Math.hypot(
    nearestCupPoint.x - course.cup.x,
    nearestCupPoint.y - course.cup.y,
  );
  const onGreen = !ball.airborne && Math.abs(ball.z - elevationAt(course.cup, course)) < 2;
  if (onGreen && cupDistance <= CUP_RADIUS - BALL_RADIUS * 0.12 && speedOf(ball) <= 360) {
    ball.holed = true;
    ball.x = course.cup.x;
    ball.y = course.cup.y;
    ball.z = elevationAt(course.cup, course);
    ball.vx = ball.vy = ball.vz = 0;
    return { type: "holed" };
  }
  // The uphill turning point is still in motion: only a stable lie ends a shot.
  if (!ball.airborne && speedOf(ball) < 3 && groundForces(ball, course).canRest) {
    ball.vx = ball.vy = ball.vz = 0;
    ball.lie = { x: ball.x, y: ball.y };
    return { type: "rest" };
  }
  if (launched) return { type: "launch" };
  if (landed) return { type: "land", impact };
  if (boosted) return { type: "boost", speed: speedOf(ball) };
  return {
    type: impact > 0 ? "bounce" : "rolling",
    impact,
    fastCup: onGreen && cupDistance < CUP_RADIUS,
  };
}

function resolveWalls(ball: Ball, course: PreparedCourse, previous: Point = ball): number {
  let impact = 0;

  for (let pass = 0; pass < 2; pass += 1) {
    for (const wall of course.walls) {
      const nearestPoint = closest(wall.a, wall.b, ball);
      if (ballHeight(ball, course) > elevationAt(nearestPoint, course) + 26) continue;
      let dx = ball.x - nearestPoint.x;
      let dy = ball.y - nearestPoint.y;
      let distance = Math.hypot(dx, dy);

      if (distance >= BALL_RADIUS) {
        continue;
      }

      if (distance < 0.00001) {
        dx = previous.x - nearestPoint.x;
        dy = previous.y - nearestPoint.y;
        distance = Math.hypot(dx, dy) || 1;
      }

      const normalX = dx / distance;
      const normalY = dy / distance;
      ball.x = nearestPoint.x + normalX * (BALL_RADIUS + 0.01);
      ball.y = nearestPoint.y + normalY * (BALL_RADIUS + 0.01);

      const approach = ball.vx * normalX + ball.vy * normalY;
      if (approach < 0) {
        impact = Math.max(impact, -approach);
        ball.vx -= 1.76 * approach * normalX;
        ball.vy -= 1.76 * approach * normalY;
        ball.vx *= 0.985;
        ball.vy *= 0.985;
      }
    }
  }

  return impact;
}

function resolveBumpers(ball: Ball, course: PreparedCourse, previous: Point = ball): number {
  let impact = 0;
  for (const bumper of course.bumpers ?? []) {
    if (ballHeight(ball, course) > elevationAt(bumper, course) + 12) continue;
    let dx = ball.x - bumper.x;
    let dy = ball.y - bumper.y;
    let distance = Math.hypot(dx, dy);
    const clearance = BALL_RADIUS + bumper.radius;
    if (distance >= clearance) continue;

    if (distance < 0.00001) {
      dx = previous.x - bumper.x;
      dy = previous.y - bumper.y;
      distance = Math.hypot(dx, dy) || 1;
    }
    const normalX = dx / distance;
    const normalY = dy / distance;
    ball.x = bumper.x + normalX * (clearance + 0.01);
    ball.y = bumper.y + normalY * (clearance + 0.01);

    const approach = ball.vx * normalX + ball.vy * normalY;
    if (approach >= 0) continue;
    impact = Math.max(impact, -approach);
    ball.vx -= 1.82 * approach * normalX;
    ball.vy -= 1.82 * approach * normalY;
    ball.vx += normalX * bumper.kick;
    ball.vy += normalY * bumper.kick;
  }
  return impact;
}

export function stepWorld(balls: Ball[], course: PreparedCourse, dt = DT): SimulationEvent[] {
  const events = balls.map((ball) => step(ball, course, dt));

  // Everyone shares the exact tee. Release protection only once a ball has left
  // the tee and cleared its neighbours, including simultaneous identical shots.
  for (const ball of balls) {
    if (
      ball.teeProtected &&
      Math.hypot(ball.x - course.start.x, ball.y - course.start.y) > BALL_RADIUS * 3 &&
      balls.every(
        (other) =>
          other === ball ||
          other.holed ||
          Math.hypot(ball.x - other.x, ball.y - other.y) > BALL_RADIUS * 2 + 1,
      )
    ) {
      ball.teeProtected = false;
    }
  }

  // Equal-mass sphere impulses let a moving ball push a stationary opponent.
  // Iterative separation handles groups of balls and rail-adjacent contacts.
  for (let pass = 0; pass < 3; pass += 1) {
    for (let firstIndex = 0; firstIndex < balls.length; firstIndex += 1) {
      for (let secondIndex = firstIndex + 1; secondIndex < balls.length; secondIndex += 1) {
        const first = balls[firstIndex];
        const second = balls[secondIndex];
        if (
          !first ||
          !second ||
          first.holed ||
          second.holed ||
          first.teeProtected ||
          second.teeProtected ||
          Math.abs(ballHeight(first, course) - ballHeight(second, course)) >= BALL_RADIUS * 2
        ) {
          continue;
        }

        const dx = second.x - first.x;
        const dy = second.y - first.y;
        const distance = Math.hypot(dx, dy);
        if (distance >= BALL_RADIUS * 2) {
          continue;
        }

        const normalX = distance > 1e-6 ? dx / distance : 1;
        const normalY = distance > 1e-6 ? dy / distance : 0;
        const oldFirst = { x: first.x, y: first.y };
        const oldSecond = { x: second.x, y: second.y };
        const overlap = (BALL_RADIUS * 2 - distance + 0.02) / 2;

        first.x -= normalX * overlap;
        first.y -= normalY * overlap;
        second.x += normalX * overlap;
        second.y += normalY * overlap;

        const relativeVelocity =
          (second.vx - first.vx) * normalX + (second.vy - first.vy) * normalY;
        if (relativeVelocity < 0) {
          const impulse = -relativeVelocity * 0.97;
          first.vx -= impulse * normalX;
          first.vy -= impulse * normalY;
          second.vx += impulse * normalX;
          second.vy += impulse * normalY;

          if (-relativeVelocity > 25) {
            events[firstIndex] = { type: "collision", impact: -relativeVelocity };
            events[secondIndex] = { type: "collision", impact: -relativeVelocity };
          }
        }

        const firstImpact = Math.max(
          resolveWalls(first, course, oldFirst),
          resolveBumpers(first, course, oldFirst),
        );
        const secondImpact = Math.max(
          resolveWalls(second, course, oldSecond),
          resolveBumpers(second, course, oldSecond),
        );
        settleRicochetLoop(first, firstImpact);
        settleRicochetLoop(second, secondImpact);
      }
    }
  }

  return events;
}

export function velocityForPull(dx: number, dy: number, maxPull: number): ShotVelocity {
  const distance = Math.hypot(dx, dy);
  const power = clamp((distance - 4) / (maxPull - 4), 0, 1);
  const speed = MAX_SPEED * Math.sqrt(power);

  return {
    vx: distance ? (dx / distance) * speed : 0,
    vy: distance ? (dy / distance) * speed : 0,
    power,
  };
}

export function predict(ball: Ball, velocity: Velocity, course: PreparedCourse): Prediction {
  const state: Ball = {
    ...ball,
    ...velocity,
    time: 0,
    holed: false,
  };
  const points: FlightPoint[] = [{ x: state.x, y: state.y, height: ballHeight(state, course) }];

  for (let index = 0; index < 720; index += 1) {
    const result = step(state, course);
    if (index % 5 === 0 || result.type === "bounce") {
      points.push({ x: state.x, y: state.y, height: ballHeight(state, course) });
    }
    if (result.type === "rest" || result.type === "holed" || result.type === "reset") {
      break;
    }
  }

  points.push({ x: state.x, y: state.y, height: ballHeight(state, course) });
  return { points, holed: state.holed, end: state };
}
